/**
 * Musical furniture (pianos, organ): tapping one outside build mode plays its
 * melody; tapping again — or tapping another instrument — stops / switches it.
 * One shared player, so the house never plays two pieces on top of each other.
 * Pianos: Debussy's Clair de Lune (opening theme, 42 s); organ: the Interstellar
 * organ theme (21.5 s, from the organ's entry). Both fade in/out and sit at
 * -16 LUFS like the other game sounds.
 */
import type * as PIXI from 'pixi.js';
import useSoundStore from '../../../stores/useSoundStore';
import { MUSICAL_INSTRUMENT_MELODIES, isMusicalInstrumentCatalog } from '../../../data/furniture';

const MELODY_VOLUME = 0.6;

let player: HTMLAudioElement | null = null;
let playingCatalogId: number | null = null;

/** What is playing (for the music-notes effect), or null when the house is quiet. */
export type InstrumentPlaying = { sprite: PIXI.Sprite; catalogId: number } | null;
const listeners = new Set<(playing: InstrumentPlaying) => void>();
const notify = (playing: InstrumentPlaying) => listeners.forEach((l) => l(playing));

/** Subscribe to melody start/stop; returns the unsubscribe. */
export function onInstrumentMelody(listener: (playing: InstrumentPlaying) => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export const isMusicalInstrument = isMusicalInstrumentCatalog;

export function stopInstrumentMelody(): void {
  if (player) {
    player.pause();
    player.onended = null;
    player = null;
  }
  if (playingCatalogId !== null) {
    playingCatalogId = null;
    useSoundStore.getState().setOceanDucked(false);
    notify(null);
  }
}

/** Play the instrument's melody, or stop it if this instrument is already playing. */
export function toggleInstrumentMelody(catalogId: number, sprite?: PIXI.Sprite): void {
  const src = MUSICAL_INSTRUMENT_MELODIES[catalogId];
  if (!src) return;
  const wasPlaying = playingCatalogId === catalogId;
  stopInstrumentMelody();
  if (wasPlaying || !useSoundStore.getState().soundsEnabled) return;

  const audio = new Audio(src);
  audio.volume = MELODY_VOLUME;
  audio.onended = () => {
    if (player === audio) stopInstrumentMelody();
  };
  player = audio;
  playingCatalogId = catalogId;
  // Let the music be heard over the ocean ambience.
  useSoundStore.getState().setOceanDucked(true);
  if (sprite) notify({ sprite, catalogId });
  audio.play().catch(() => {
    if (player === audio) stopInstrumentMelody();
  });
}

/**
 * Attach the "tap to play" gesture to an instrument sprite. Works for every
 * visitor (not only the owner); ignored in build mode, where a press drags.
 * A press that moved or was held (viewport pan, long press) is not a tap.
 */
export function attachInstrumentTap(
  sprite: PIXI.Sprite,
  catalogId: number,
  isBuildMode: () => boolean,
): void {
  if (!isMusicalInstrument(catalogId)) return;
  let down: { x: number; y: number; t: number } | null = null;
  sprite.on('pointerdown', (e: PIXI.FederatedPointerEvent) => {
    down = { x: e.clientX, y: e.clientY, t: Date.now() };
  });
  sprite.on('pointerup', (e: PIXI.FederatedPointerEvent) => {
    const start = down;
    down = null;
    if (!start || isBuildMode()) return;
    const moved = Math.hypot(e.clientX - start.x, e.clientY - start.y);
    if (moved > 10 || Date.now() - start.t > 400) return;
    toggleInstrumentMelody(catalogId, sprite);
  });
}
